// frontend/src/pages/system/PtpLinks.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NetworkCheck as PingIcon,
  Lan as LanIcon,
  CheckCircle as OnlineIcon,
  Warning as WarningIcon,
  Error as OfflineIcon,
  Router as RouterIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

const PtpLinks = () => {
  const [links, setLinks] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [pingingId, setPingingId] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    source_node_id: '',
    target_node_id: '',
    master_ip: '',
    master_username: 'admin',
    master_password: '.-g3m1n1s23-.',
    station_ip: '',
    station_username: 'admin',
    station_password: '.-g3m1n1s23-.',
    radio_model: 'Mimosa C5c',
    frequency_mhz: '',
    channel_width_mhz: '',
    target_subnet: '',
    notes: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ptpRes, nodesRes] = await Promise.all([
        fetch('/api/ptp-links', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
        fetch('/api/nodes', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      ]);

      if (ptpRes.ok) {
        const ptpData = await ptpRes.json();
        setLinks(ptpData);
      }
      if (nodesRes.ok) {
        const nodesData = await nodesRes.json();
        setNodes(nodesData);
      }
    } catch (err) {
      console.error('Error fetching PtP links:', err);
      setError('Error al cargar la lista de enlaces troncales: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (link = null) => {
    if (link) {
      setSelectedLink(link);
      setFormData({
        name: link.name || '',
        source_node_id: link.source_node_id || '',
        target_node_id: link.target_node_id || '',
        master_ip: link.master_ip || '',
        master_username: link.master_username || 'admin',
        master_password: link.master_password || '.-g3m1n1s23-.',
        station_ip: link.station_ip || '',
        station_username: link.station_username || 'admin',
        station_password: link.station_password || '.-g3m1n1s23-.',
        radio_model: link.radio_model || 'Mimosa C5c',
        frequency_mhz: link.frequency_mhz || '',
        channel_width_mhz: link.channel_width_mhz || '',
        target_subnet: link.target_subnet || '',
        notes: link.notes || ''
      });
    } else {
      setSelectedLink(null);
      setFormData({
        name: '',
        source_node_id: '',
        target_node_id: '',
        master_ip: '',
        master_username: 'admin',
        master_password: '.-g3m1n1s23-.',
        station_ip: '',
        station_username: 'admin',
        station_password: '.-g3m1n1s23-.',
        radio_model: 'Mimosa C5c',
        frequency_mhz: '',
        channel_width_mhz: '',
        target_subnet: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      setError('El nombre del enlace troncal es obligatorio');
      return;
    }

    try {
      const url = selectedLink ? `/api/ptp-links/${selectedLink.id}` : '/api/ptp-links';
      const method = selectedLink ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al guardar enlace PtP');

      setSuccessMsg(selectedLink ? 'Enlace troncal actualizado correctamente' : 'Nuevo enlace troncal registrado exitosamente');
      setOpenDialog(false);
      fetchInitialData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    if (!linkToDelete) return;
    try {
      const response = await fetch(`/api/ptp-links/${linkToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Error al eliminar enlace troncal');

      setSuccessMsg('Enlace troncal eliminado correctamente');
      setDeleteConfirmOpen(false);
      setLinkToDelete(null);
      fetchInitialData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePingTest = async (id) => {
    setPingingId(id);
    setError(null);
    try {
      const response = await fetch(`/api/ptp-links/${id}/ping`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Error al realizar ping al enlace');

      setSuccessMsg(`Ping completado: Master (${data.master.ip}): ${data.master.alive ? data.master.latency_ms + 'ms 🟢' : 'Sin respuesta 🔴'} | Station (${data.station.ip}): ${data.station.alive ? data.station.latency_ms + 'ms 🟢' : 'Sin respuesta 🔴'}`);
      fetchInitialData();
    } catch (err) {
      setError(err.message);
    } finally {
      setPingingId(null);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Troncal PtP',
      width: 240,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanIcon color="primary" />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{params.value}</Typography>
            <Typography variant="caption" color="text.secondary">{params.row.radio_model || 'Mimosa C5c'}</Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'route',
      headerName: 'Ruta de Nodos',
      width: 230,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.row.source_node_name || 'Nodo Origen'} ➔ {params.row.target_node_name || 'Nodo Destino'}
        </Typography>
      )
    },
    {
      field: 'master_ip',
      headerName: 'Radio Master (Origen)',
      width: 170,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{params.value || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary">Master (AP)</Typography>
        </Box>
      )
    },
    {
      field: 'station_ip',
      headerName: 'Radio Station (Destino)',
      width: 170,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{params.value || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary">Station (Receptor)</Typography>
        </Box>
      )
    },
    {
      field: 'target_subnet',
      headerName: 'Subred Alimentada',
      width: 160,
      renderCell: (params) => (
        <Chip
          icon={<RouterIcon fontSize="small" />}
          label={params.value || 'N/A'}
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      )
    },
    {
      field: 'status',
      headerName: 'Estado Latencia',
      width: 150,
      renderCell: (params) => {
        const isOnline = params.value === 'online';
        const isWarn = params.value === 'warning';
        return (
          <Chip
            icon={isOnline ? <OnlineIcon /> : isWarn ? <WarningIcon /> : <OfflineIcon />}
            label={isOnline ? `${params.row.latency_ms ? params.row.latency_ms + ' ms' : 'En línea'}` : isWarn ? 'Parcial' : 'Caído'}
            color={isOnline ? 'success' : isWarn ? 'warning' : 'error'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Probar Ping / Latencia">
            <IconButton
              size="small"
              color="info"
              onClick={() => handlePingTest(params.row.id)}
              disabled={pingingId === params.row.id}
            >
              {pingingId === params.row.id ? <CircularProgress size={18} /> : <PingIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Editar Enlace">
            <IconButton size="small" onClick={() => handleOpenForm(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Eliminar Enlace">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setLinkToDelete(params.row);
                setDeleteConfirmOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      {/* Cabecera Principal */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
            Enlaces Troncales Punto a Punto (PtP)
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontWeight: 500 }}>
            Supervise y configure los enlaces de fibra/radio (Backhaul PtP) que interconectan los Nodos de su red.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            py: 1.2,
            backgroundColor: '#0284c7',
            boxShadow: '0 4px 12px rgba(2, 132, 199, 0.2)',
            '&:hover': { backgroundColor: '#0369a1' }
          }}
        >
          Nuevo Enlace Troncal
        </Button>
      </Box>

      {/* Tarjetas de Resumen */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#e0f2fe', color: '#0284c7' }}>
                <LanIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>ENLACES TRONCALES PTP</Typography>
                <Typography variant="h5" fontWeight={800}>{links.length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#dcfce7', color: '#15803d' }}>
                <OnlineIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>ESTADO EN LÍNEA</Typography>
                <Typography variant="h5" fontWeight={800} color="success.main">
                  {links.filter(l => l.status === 'online').length} / {links.length}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#fef3c7', color: '#d97706' }}>
                <RouterIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>EQUIPAMIENTO DESTACADO</Typography>
                <Typography variant="h6" fontWeight={800} color="#92400e">
                  Mimosa C5c & Ubiquiti
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '16px' }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* Tabla DataGrid */}
      <Paper sx={{ borderRadius: '24px', overflow: 'hidden', p: 1, border: '1px solid rgba(0,0,0,0.05)' }}>
        <DataGrid
          rows={links}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          autoHeight
          loading={loading}
          disableSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { borderBottom: '1px solid #f1f5f9' },
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc', borderRadius: '12px' }
          }}
        />
      </Paper>

      {/* Modal Formulario Crear / Editar */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
          {selectedLink ? 'Editar Enlace Troncal PtP' : 'Nuevo Enlace Troncal Punto a Punto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Enlace Troncal"
                placeholder="ej. Troncal Nuevo Sullana ➔ Miraflores"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Nodo de Origen"
                value={formData.source_node_id}
                onChange={(e) => setFormData({ ...formData, source_node_id: e.target.value })}
              >
                <MenuItem value="">-- Ninguno --</MenuItem>
                {nodes.map(n => (
                  <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Nodo de Destino"
                value={formData.target_node_id}
                onChange={(e) => setFormData({ ...formData, target_node_id: e.target.value })}
              >
                <MenuItem value="">-- Ninguno --</MenuItem>
                {nodes.map(n => (
                  <MenuItem key={n.id} value={n.id}>{n.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Radio Master (Emisor) IP"
                placeholder="ej. 192.168.10.177"
                value={formData.master_ip}
                onChange={(e) => setFormData({ ...formData, master_ip: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Radio Station (Receptor) IP"
                placeholder="ej. 192.168.10.178"
                value={formData.station_ip}
                onChange={(e) => setFormData({ ...formData, station_ip: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contraseña Radios Mimosa / Ubiquiti"
                type="password"
                value={formData.master_password}
                onChange={(e) => setFormData({ ...formData, master_password: e.target.value, station_password: e.target.value })}
                helperText="Clave por defecto: .-g3m1n1s23-."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Modelo de los Radios"
                value={formData.radio_model}
                onChange={(e) => setFormData({ ...formData, radio_model: e.target.value })}
                placeholder="ej. Mimosa C5c, Ubiquiti airFiber"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subred Servida (MikroTik Destino)"
                placeholder="ej. 192.168.20.0/24"
                value={formData.target_subnet}
                onChange={(e) => setFormData({ ...formData, target_subnet: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Frecuencia (MHz)"
                placeholder="ej. 5800"
                type="number"
                value={formData.frequency_mhz}
                onChange={(e) => setFormData({ ...formData, frequency_mhz: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Ancho de Canal (MHz)"
                placeholder="ej. 80"
                type="number"
                value={formData.channel_width_mhz}
                onChange={(e) => setFormData({ ...formData, channel_width_mhz: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notas / Observaciones"
                placeholder="Información adicional del enlace troncal..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary" sx={{ borderRadius: '8px', fontWeight: 700 }}>
            {selectedLink ? 'Guardar Cambios' : 'Crear Enlace Troncal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmar Eliminación */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Está seguro de eliminar el enlace troncal <strong>"{linkToDelete?.name}"</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PtpLinks;
